/**
 * Shared utilities for the Data Explorer feature.
 * Loaded before page-specific scripts via <script> tag in templates.
 * All exports are assigned to window.* globals.
 */

// ── Competitor data helpers ──

const formatCompetitionReason = (reason) => {
  const map = {
    'organic_keyword_overlap': 'Organic keyword overlap',
    'product_overlap': 'Product overlap',
  };
  return map[reason] || (reason || '').replace(/_/g, ' ');
};

const getCompetitorName = (c) => {
  if (c.name) return c.name;
  const d = c.domain || c.website || '';
  const cleaned = d.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  return cleaned.split('.')[0].charAt(0).toUpperCase() + cleaned.split('.')[0].slice(1);
};

const getCompetitorDomain = (c) => {
  if (c.domain) return c.domain;
  const d = c.website || c.company_details_url || '';
  return d.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
};

// ── Website click handler ──

const handleWebsiteClick = (e, website) => {
  e.stopPropagation();
  const url = website.startsWith('http://') || website.startsWith('https://')
    ? website
    : `https://${website}`;
  window.open(url, '_blank');
};

// ── Smart search helpers ──

const DASHBOARD_EXPLORER_BASE = '/dashboard/explorer';

const normalizeExplorerDomain = (input) => {
  if (window.DomainValidator) {
    const result = window.DomainValidator.validate(input);
    return result.valid ? result.domain : null;
  }

  let domain = String(input || '').trim().toLowerCase();
  domain = domain.replace(/^https?:\/\//, '');
  domain = domain.replace(/^www\./, '');
  domain = domain.split('/')[0];
  domain = domain.split(':')[0];
  domain = domain.split('?')[0];

  if (!domain || !domain.includes('.')) return null;
  const parts = domain.split('.');
  const tld = parts[parts.length - 1];
  if (!tld || tld.length < 2) return null;
  if (!/^[a-z0-9.-]+$/.test(domain)) return null;
  if (parts.some(part => !part || part.startsWith('-') || part.endsWith('-'))) return null;
  return domain;
};

const detectExplorerInputType = (query) => {
  const text = String(query || '').trim();
  if (!text) return 'empty';
  if (text.includes('@') && /\.[a-z]{2,}/i.test(text)) return 'email';
  if (!/\s/.test(text) && normalizeExplorerDomain(text)) return 'domain';
  return 'free';
};

const splitExplorerPersonName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
};

const buildExplorerIntentSuggestions = (query) => {
  const text = String(query || '').trim();
  const type = detectExplorerInputType(text);
  if (type === 'email') {
    return [{ kind: 'person-email', icon: 'ph-envelope-simple', label: 'Search for person behind', highlight: text }];
  }
  if (type === 'domain') {
    return [{
      kind: 'company-domain',
      icon: 'ph-globe',
      label: 'Search company by website',
      highlight: text,
      value: normalizeExplorerDomain(text) || text,
    }];
  }
  if (type === 'free') {
    return [{ kind: 'person-name', icon: 'ph-user', label: 'Search person by name', highlight: text }];
  }
  return [];
};

const buildExplorerCompanySuggestions = (results) => (
  (results || [])
    .filter(company => company.domain || company.slug)
    .map(company => ({ ...company, kind: 'company-result' }))
);

const searchExplorerCompanies = (query, limit = 6) => (
  fetch(`/api/explorer/search?q=${encodeURIComponent(query)}`)
    .then(response => response.json())
    .then(data => (data.results || []).slice(0, limit))
);

const getExplorerDefaultSubmitItem = (query, companySuggestions) => {
  const text = String(query || '').trim();
  const type = detectExplorerInputType(text);
  if (type === 'email') return { kind: 'person-email', highlight: text };
  if (type === 'domain') {
    return { kind: 'company-domain', highlight: text, value: normalizeExplorerDomain(text) || text };
  }
  if (type === 'free' && companySuggestions && companySuggestions.length > 0) {
    return companySuggestions[0];
  }
  return null;
};

const getExplorerSelectionAction = (item, options = {}) => {
  if (!item) return { type: 'noop' };

  if (item.kind === 'person-email') {
    return { type: 'person-lookup', params: { work_email: item.highlight } };
  }

  if (item.kind === 'person-name') {
    const employer = String(item.company || options.personCompany || '').trim();
    if (!employer) return { type: 'needs-person-company' };
    return {
      type: 'person-lookup',
      params: {
        ...splitExplorerPersonName(item.highlight),
        employer_website: employer,
      },
    };
  }

  if (item.kind === 'company-domain') {
    return { type: 'company', value: item.value || normalizeExplorerDomain(item.highlight) || item.highlight };
  }

  if (item.kind === 'company-result') {
    return { type: 'company', value: item.domain || item.slug };
  }

  if (item.kind === 'person-shortcut') {
    if (item.slug) return { type: 'person-slug', value: item.slug };
    return { type: 'person-lookup', params: { work_email: item.work_email || 'steven@nubela.co' } };
  }

  if (item.kind === 'company-shortcut' && item.domain) {
    return { type: 'company', value: item.domain };
  }

  if (item.slug) return { type: 'person-slug', value: item.slug };
  if (item.domain) return { type: 'company', value: item.domain };
  return { type: 'noop' };
};

const getExplorerCompanyHref = (value, options = {}) => {
  const clean = normalizeExplorerDomain(value) || String(value || '').trim();
  if (!clean) return '';
  const dashboard = options.dashboard !== false;
  const base = dashboard ? `${DASHBOARD_EXPLORER_BASE}/` : '/companies/';
  return base + encodeURIComponent(clean);
};

const navigateExplorerCompany = (value, options = {}) => {
  const href = getExplorerCompanyHref(value, options);
  if (href) window.location.href = href;
};

const getExplorerPersonLookupHref = (params) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    const clean = String(value || '').trim();
    if (clean) query.set(key, clean);
  });
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return `${DASHBOARD_EXPLORER_BASE}/person/lookup${suffix}`;
};

const navigateExplorerPersonLookup = (params) => {
  window.location.href = getExplorerPersonLookupHref(params);
};

const getExplorerPersonSlugHref = (slug) => {
  if (!slug) return '';
  return `${DASHBOARD_EXPLORER_BASE}/person/${encodeURIComponent(slug)}`;
};

const navigateExplorerPersonSlug = (slug) => {
  const href = getExplorerPersonSlugHref(slug);
  if (href) window.location.href = href;
};

const getExplorerSearchActionHref = (action, options = {}) => {
  if (!action || action.type === 'noop' || action.type === 'needs-person-company') return '';
  if (action.type === 'person-lookup') return getExplorerPersonLookupHref(action.params);
  if (action.type === 'person-slug') return getExplorerPersonSlugHref(action.value);
  if (action.type === 'company') return getExplorerCompanyHref(action.value, options);
  return '';
};

const navigateExplorerSearchAction = (action, options = {}) => {
  const href = getExplorerSearchActionHref(action, options);
  if (href) window.location.href = href;
};

// ── CSV export ──

const downloadCsv = (headers, rows, filename) => {
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);
};

// ── Export to window ──

window.formatCompetitionReason = formatCompetitionReason;
window.getCompetitorName = getCompetitorName;
window.getCompetitorDomain = getCompetitorDomain;
window.handleWebsiteClick = handleWebsiteClick;
window.downloadCsv = downloadCsv;
window.DataExplorerSearch = {
  detectInputType: detectExplorerInputType,
  normalizeDomain: normalizeExplorerDomain,
  splitPersonName: splitExplorerPersonName,
  buildIntentSuggestions: buildExplorerIntentSuggestions,
  buildCompanySuggestions: buildExplorerCompanySuggestions,
  searchCompanies: searchExplorerCompanies,
  getDefaultSubmitItem: getExplorerDefaultSubmitItem,
  getSelectionAction: getExplorerSelectionAction,
  getActionHref: getExplorerSearchActionHref,
  getCompanyHref: getExplorerCompanyHref,
  getPersonLookupHref: getExplorerPersonLookupHref,
  getPersonSlugHref: getExplorerPersonSlugHref,
  navigateAction: navigateExplorerSearchAction,
  navigateToCompany: navigateExplorerCompany,
  navigateToPersonLookup: navigateExplorerPersonLookup,
  navigateToPersonSlug: navigateExplorerPersonSlug,
};
